(function (globalObject) {
  'use strict';

  const inFlight = new WeakMap();
  const collectionSelector = '[data-interdeal-collection]';
  const collectionIdAttribute = 'data-interdeal-collection-id';
  const gridSelector = '[data-interdeal-collection-grid]';
  const controlSelector = '[data-interdeal-load-more-control]';
  const linkSelector = '[data-interdeal-load-more-link]';
  const statusSelector = '[data-interdeal-load-more-status]';
  const mobileFiltersSelector = '[data-interdeal-mobile-filters]';
  const mobileFilterPanelSelector = '[data-interdeal-filter-panel]';
  const mobileFilterTriggerSelector = '[data-interdeal-filter-trigger]';
  const mobileFilterCloseSelector = '[data-interdeal-filter-close]';
  const mobileFilterBackdropSelector = '[data-interdeal-filter-backdrop]';
  const initializedFilterRoots = new WeakSet();
  const activeMobileFilterByDocument = new WeakMap();

  function attribute(element, name) {
    return element && typeof element.getAttribute === 'function' ? element.getAttribute(name) : null;
  }

  function message(status, name, fallback) {
    return attribute(status, `data-${name}-label`) || fallback;
  }

  function isArticle(element) {
    return element && element.tagName === 'ARTICLE';
  }

  function parseCollectionResponse(body, expectedCollectionId, Parser) {
    if (typeof Parser !== 'function') return null;

    const document = new Parser().parseFromString(body, 'text/html');
    const collections = Array.from(document.querySelectorAll(collectionSelector));
    const collection = collections.find((candidate) => (
      attribute(candidate, collectionIdAttribute) === expectedCollectionId
    ));

    if (!collection) return null;

    const responseGrid = collection.querySelector(gridSelector);
    if (!responseGrid) {
      return { collectionId: expectedCollectionId, grid: null, cards: [], nextUrl: undefined };
    }

    const cards = Array.from(responseGrid.children).filter(isArticle);
    const nextLink = collection.querySelector(linkSelector);
    let nextUrl = null;
    if (nextLink) nextUrl = attribute(nextLink, 'href') ?? '';

    return {
      collectionId: attribute(collection, collectionIdAttribute),
      grid: responseGrid,
      cards,
      nextUrl,
    };
  }

  function loadNext({ control, link, grid, fetcher, parseHtml }) {
    if (control && inFlight.has(control)) return inFlight.get(control);

    const currentUrl = attribute(link, 'href');
    const collectionId = attribute(grid, collectionIdAttribute);
    const status = control && typeof control.querySelector === 'function'
      ? control.querySelector(statusSelector)
      : null;

    if (!control || !link || !grid || !currentUrl || !collectionId
      || typeof fetcher !== 'function' || typeof parseHtml !== 'function') {
      return Promise.resolve({
        appended: 0,
        nextUrl: currentUrl,
        complete: false,
        error: 'Load More is not configured for this collection.',
      });
    }

    grid.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');
    if (status) status.textContent = message(status, 'loading', 'Loading more products…');

    let requestPromise;
    requestPromise = Promise.resolve().then(async () => {
      try {
        const response = await fetcher(currentUrl, { headers: { Accept: 'text/html' } });
        if (!response || response.ok !== true) throw new Error('The next collection page could not be loaded.');

        const body = await response.text();
        if (typeof body !== 'string' || body.trim() === '') throw new Error('The next collection page was empty.');

        const parsed = await parseHtml(body);
        if (!parsed || parsed.collectionId !== collectionId || !parsed.grid
          || attribute(parsed.grid, collectionIdAttribute) !== collectionId
          || !Array.isArray(parsed.cards)
          || !Object.hasOwn(parsed, 'nextUrl')) {
          throw new Error('The next collection page did not contain the matching collection.');
        }

        if (parsed.nextUrl !== null
          && (typeof parsed.nextUrl !== 'string' || parsed.nextUrl.trim() === '')) {
          throw new Error('The next-page link is malformed.');
        }

        const cardSet = new Set(parsed.cards);
        const cards = Array.from(parsed.grid.children).filter((card) => (
          isArticle(card)
          && (card.parentElement === parsed.grid || card.parentNode === parsed.grid)
          && cardSet.has(card)
        ));
        if (cards.length === 0) throw new Error('The next collection page contained no product cards.');

        const appendedCards = [];
        try {
          for (const card of cards) {
            grid.appendChild(card);
            appendedCards.push(card);
          }
        } catch (error) {
          appendedCards.forEach((card) => card.remove());
          throw error;
        }

        const nextUrl = parsed.nextUrl;
        if (nextUrl === null) {
          link.remove();
          if (status) status.textContent = message(status, 'complete', 'All products loaded.');
        } else {
          link.setAttribute('href', nextUrl);
          const label = message(
            status,
            cards.length === 1 ? 'loaded-one' : 'loaded-other',
            cards.length === 1 ? 'more product loaded.' : 'more products loaded.',
          );
          if (status) status.textContent = `${cards.length} ${label}`;
        }

        return { appended: cards.length, nextUrl, complete: nextUrl === null, error: null };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'The next collection page could not be loaded.';
        if (status) status.textContent = message(status, 'error', "Couldn't load more products. Use the link to retry.");
        return { appended: 0, nextUrl: currentUrl, complete: false, error: errorMessage };
      } finally {
        grid.setAttribute('aria-busy', 'false');
        link.removeAttribute('aria-disabled');
        if (inFlight.get(control) === requestPromise) inFlight.delete(control);
      }
    });

    inFlight.set(control, requestPromise);
    return requestPromise;
  }

  function isPlainActivation(event, link) {
    return event.button === 0
      && !event.defaultPrevented
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey
      && !link.hasAttribute('target')
      && !link.hasAttribute('download');
  }

  function isInsidePanel(element, panel) {
    let current = element;
    while (current) {
      if (current === panel) return true;
      current = current.parentNode;
    }
    return false;
  }

  function isHiddenByAncestor(element, panel) {
    let current = element;
    while (current && current !== panel) {
      if (current.hidden === true || attribute(current, 'aria-hidden') === 'true') return true;
      if (current.tagName === 'DETAILS' && current.open !== true) return true;
      current = current.parentNode;
    }
    return current !== panel;
  }

  function initializeMobileFilters({ root, documentRef, windowRef } = {}) {
    if (!root || typeof root.querySelector !== 'function' || initializedFilterRoots.has(root)) return;

    const details = root.querySelector(mobileFiltersSelector);
    const panel = details && details.querySelector(mobileFilterPanelSelector);
    const summary = details && details.querySelector(mobileFilterTriggerSelector);
    const closeButton = panel && panel.querySelector(mobileFilterCloseSelector);
    const backdrop = details && details.querySelector(mobileFilterBackdropSelector);
    const doc = documentRef || (windowRef && windowRef.document) || globalObject.document;
    if (!details || !panel || !summary || !closeButton || !backdrop || !doc || !doc.body || !doc.body.style) return;

    initializedFilterRoots.add(root);
    let isOpen = false;
    let previousOverflow = '';
    let closeDrawer;

    const getFocusable = () => Array.from(panel.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]',
    )).filter((element) => (
      element.hidden !== true
      && element.disabled !== true
      && !(typeof element.hasAttribute === 'function' && element.hasAttribute('disabled'))
      && attribute(element, 'aria-hidden') !== 'true'
      && attribute(element, 'tabindex') !== '-1'
      && !isHiddenByAncestor(element, panel)
    ));

    const openDrawer = () => {
      if (isOpen) return;
      const activeClose = activeMobileFilterByDocument.get(doc);
      if (activeClose && activeClose !== closeDrawer) activeClose();
      isOpen = true;
      previousOverflow = doc.body.style.overflow;
      activeMobileFilterByDocument.set(doc, closeDrawer);
      doc.body.style.overflow = 'hidden';
      panel.setAttribute('aria-modal', 'true');
      const focusable = getFocusable();
      const initialFocus = focusable[0] || panel;
      if (typeof initialFocus.focus === 'function') initialFocus.focus();
    };

    closeDrawer = () => {
      if (!isOpen && !details.open) return;
      details.open = false;
      panel.removeAttribute('aria-modal');
      if (isOpen) {
        doc.body.style.overflow = previousOverflow;
        if (activeMobileFilterByDocument.get(doc) === closeDrawer) {
          activeMobileFilterByDocument.delete(doc);
        }
      }
      isOpen = false;
      if (typeof summary.focus === 'function') summary.focus();
    };

    details.addEventListener('toggle', () => {
      if (details.open) openDrawer();
      else closeDrawer();
    });
    closeButton.addEventListener('click', closeDrawer);
    backdrop.addEventListener('click', closeDrawer);
    doc.addEventListener('keydown', (event) => {
      if (!details.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        if (typeof panel.focus === 'function') panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = doc.activeElement;
      if (event.shiftKey && (active === first || !isInsidePanel(active, panel))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !isInsidePanel(active, panel))) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initialize(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    const collections = root.matches && root.matches(collectionSelector)
      ? [root]
      : Array.from(root.querySelectorAll(collectionSelector));
    collections.forEach((collection) => initializeMobileFilters({
      root: collection,
      documentRef: globalObject.document,
      windowRef: globalObject,
    }));

    const controls = Array.from(root.querySelectorAll(controlSelector));
    controls.forEach((control) => {
      const link = control.querySelector(linkSelector);
      const status = control.querySelector(statusSelector);
      const collection = control.closest(collectionSelector);
      const grid = collection && collection.querySelector(gridSelector);
      if (!link || !status || !grid || link.hasAttribute('data-interdeal-initialized')) return;

      link.addEventListener('click', (event) => {
        if (!isPlainActivation(event, link)) return;
        event.preventDefault();
        loadNext({
          control,
          link,
          grid,
          fetcher: (url, options) => globalObject.fetch(url, options),
          parseHtml: (body) => parseCollectionResponse(
            body,
            attribute(grid, collectionIdAttribute),
            globalObject.DOMParser,
          ),
        });
      });
      link.setAttribute('data-interdeal-initialized', 'true');
    });
  }

  globalObject.InterdealCollection = { loadNext, initialize, initializeMobileFilters };

  if (globalObject.document) {
    if (globalObject.document.readyState === 'loading') {
      globalObject.document.addEventListener('DOMContentLoaded', () => initialize(globalObject.document), { once: true });
    } else {
      initialize(globalObject.document);
    }
  }
})(window);
